"use client";
import { SignedIn, SignedOut, UserButton, useUser } from "@clerk/nextjs";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { getIconClass } from "@/components/assets/icon/icons";
import { NavbarNav } from "@/components/ui/navbar-nav";
import { type TabType, useTab } from "@/lib/_context/websiteTab";

const SCROLL_THRESHOLD = 50;
const FULL_HEIGHT = 72; // matches py-6 (1.5rem top + 1.5rem bottom = 3rem = 48px, but need to account for content)
const COMPACT_HEIGHT = 47; // 35% reduction (72 * 0.65 = 46.8)

const TradieIcon = () => (
  <span
    className={`${getIconClass("tradie", "xl")}bg-custom-yellow text-custom-yellow`}
  />
);

const DiyerIcon = () => (
  <span
    className={`${getIconClass("diyer", "xl")}bg-custom-blue-dark text-custom-blue-dark`}
  />
);

const LoginIcon = () => (
  <span className={`${getIconClass("login", "xl")}text-gray-700`} />
);

const AuthIcon = () => (
  <>
    <SignedOut>
      <span className="pointer-events-none mt-3">
        <LoginIcon />
      </span>
    </SignedOut>
    <SignedIn>
      <div className="pointer-events-none mt-3">
        <UserButton />
      </div>
    </SignedIn>
  </>
);

export const NavbarMenu = () => {
  const { activeTab, setActiveTab } = useTab();
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const { isSignedIn } = useUser();
  const router = useRouter();

  useMotionValueEvent(scrollY, "change", (current) => {
    if (typeof current === "number") {
      setIsScrolled(current > SCROLL_THRESHOLD);
    }
  });

  const handleItemClick = (id: string) => {
    // If clicking on registry item and user is signed in, navigate to root
    if (id === "registry" && isSignedIn) {
      router.push("/");
      return;
    }
    // Otherwise, set the active tab as normal
    setActiveTab(id as TabType);
  };

  const navItems = [
    {
      id: "diyer",
      icon: DiyerIcon,
      label: "Diyer",
      color: "bg-custom-yellow rounded-bl-2xl",
      labelClassName: "text-custom-blue-dark p-2 font-bold",
    },
    {
      id: "tradie",
      icon: TradieIcon,
      label: "Tradie",
      color: "bg-custom-blue-dark",
      labelClassName: "text-custom-yellow p-2 font-bold",
    },
    {
      id: "registry",
      icon: AuthIcon,
      label: "Login",
      color: "bg-blue-200/40 border-b border-b-gray-900",
      labelClassName: "text-gray-900 p-2 font-bold",
    },
  ];

  return (
    <motion.div
      animate={{
        height: isScrolled ? `${COMPACT_HEIGHT}px` : `${FULL_HEIGHT}px`,
      }}
      className="flex items-stretch"
      initial={{ height: `${FULL_HEIGHT}px` }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
      }}
    >
      <NavbarNav
        activeItemId={activeTab}
        items={navItems}
        onItemClick={handleItemClick}
      />
    </motion.div>
  );
};
